import { useState, useRef, useEffect, useMemo } from 'react'
import { router, Link, usePage } from '@inertiajs/react'
import { useSelectedRow } from '@/lib/useSelectedRow'
import { SlidersHorizontal, Search, Plus, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ServerDataTable, type PagyMeta } from './_components/ServerDataTable'
import { donorColumns, type DonorRow } from './_components/DonorColumns'
import { DonorFilterSheet, type PeopleScope } from './_components/DonorFilterSheet'
import { DonorDetailSheet } from './_components/DonorDetailSheet'
import { CreateDonorSheet } from './_components/CreateDonorSheet'

interface PeopleIndexProps {
  donors: DonorRow[]
  pagy: PagyMeta
  scope: PeopleScope
  search: string | null
  q: Record<string, string> | null
  show_id?: number | null
  show_type?: 'individual' | 'organization' | null
}

const SCOPE_COPY: Record<PeopleScope, {
  title: string
  description: string
  createButton: string
  searchPlaceholder: string
}> = {
  donors: {
    title: 'Donnateurs',
    description: 'Gerez vos donnateurs individuels et organisations.',
    createButton: 'Nouveau donnateur',
    searchPlaceholder: 'Rechercher par nom, email, telephone...',
  },
  prospects: {
    title: 'Prospects',
    description: 'Suivez vos prospects individuels et organisations.',
    createButton: 'Nouveau prospect',
    searchPlaceholder: 'Rechercher par nom, email, telephone...',
  },
}

export default function PeopleIndex({ donors, pagy, scope, search, q, show_id, show_type }: PeopleIndexProps) {
  const [filterOpen, setFilterOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(search ?? '')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const copy = SCOPE_COPY[scope]
  const basePath = `/people/${scope}`
  const { auth } = usePage().props as { auth?: { user?: { role?: string } } }
  const isAdmin = auth?.user?.role === 'space_admin'

  function handleSearchChange(value: string) {
    setSearchValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      router.get(
        basePath,
        { search: value || undefined, q: q || undefined },
        { preserveState: true, preserveScroll: true }
      )
    }, 300)
  }

  const { selected, select, clear } = useSelectedRow(basePath)

  // A server-sent show_id/show_type CTA deep link becomes a normal ?selected
  // param. Strip show_id/show_type from the URL so these one-shot params don't
  // persist and re-trigger this effect (or re-open after edits).
  useEffect(() => {
    if (!show_id || !show_type) return
    const params = new URLSearchParams(window.location.search)
    params.delete('show_id')
    params.delete('show_type')
    params.set('selected', `${show_type}:${show_id}`)
    // replace (not push) so Back doesn't return to the one-shot show_id URL and
    // re-trigger this effect. Full visit (no `only`) so the donor list refreshes
    // to include a just-created/edited donor on this rare CTA-arrival path.
    router.get(`${basePath}?${params.toString()}`, {}, {
      replace: true,
      preserveState: true,
      preserveScroll: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show_id, show_type])

  // Donors are polymorphic (individual/organization), so selection is keyed by
  // `type:id`. When the selected donor isn't on the current page (off-page deep
  // link or CTA), build a minimal row — DonorDetailSheet fetches its own detail
  // by id+type, so the empty fields fill in once it loads.
  const selectedDonor = useMemo<DonorRow | null>(() => {
    if (!selected) return null
    const found = donors.find(
      (d) => String(d.id) === selected.id && d.donor_type === selected.type,
    )
    if (found) return found
    if (selected.type === 'individual' || selected.type === 'organization') {
      return {
        id: Number(selected.id),
        name: '',
        donor_type: selected.type,
        email: null,
        phone: null,
        status: { value: 0, label: '' },
        discarded: false,
      }
    }
    return null
  }, [selected, donors])
  const selectedRowId = selected?.raw ?? null

  function closeDonor() {
    clear()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{copy.title}</h1>
          <p className="text-muted-foreground">{copy.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" asChild>
              <Link href="/people/imports/organization_donors/new">
                <Upload className="mr-2 h-4 w-4" />
                Importer (XLSX)
              </Link>
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {copy.createButton}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={copy.searchPlaceholder}
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={() => setFilterOpen(true)}>
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Filtres
        </Button>
      </div>

      <ServerDataTable
        columns={donorColumns}
        data={donors}
        pagy={pagy}
        basePath={basePath}
        selectedId={selectedRowId}
        getRowId={(donor) => `${donor.donor_type}:${donor.id}`}
        onRowClick={(donor) => select(donor.id, donor.donor_type)}
      />

      <DonorDetailSheet
        donor={selectedDonor}
        onOpenChange={(open) => { if (!open) closeDonor() }}
      />

      <DonorFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        currentSearch={searchValue || null}
        currentQ={q}
        basePath={basePath}
      />

      <CreateDonorSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        scope={scope}
      />
    </div>
  )
}
